import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStripe } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { ReactComponent as BackIcon } from '../../assets/images/icons/back.svg';
import { useLocale } from '../../contexts/LocaleContext';
import { webApiClient } from '../../lib/fi-api/apiUtils';
import { ApplePayOption, buildApplePayRenewalOption } from '../../lib/applePayRecurring';
import StripeElementsProvider from '../../lib/StripeElementsProvider';
import RecoverPaymentForm, {
  BillingDetailsPrefill,
} from '../../views/ManageSubscription/Web/RecoverPayment/RecoverPaymentForm';
import styles from '../ModalEditShipping/EditShipping.module.scss';
import sheetStyles from './InAppCheckoutSheet.module.scss';

interface InAppBillingPanelProps {
  hasShipping: boolean;
  hasBilling: boolean;
  billingDetailsPrefill: BillingDetailsPrefill;
  onSubmit: (confirmationTokenId: string, stripe: Stripe) => void;
  onCancel: () => void;
  saving: boolean;
}

// klarna is pay-in-installments at purchase time and cannot back a stored payment method; the sheet
// keeps to cards, so ach stays out too and the element renders the card fields directly
const EXCLUDED_PAYMENT_METHOD_TYPES = ['klarna', 'us_bank_account'];
// with a single method the collapsed accordion row would hide the fields behind a tap
const EXPANDED_LAYOUT = { type: 'accordion', defaultCollapsed: false } as const;

interface RenewalTerms {
  amountInCents?: number | null;
  renewalMonths?: number | null;
}

function BillingForm({
  onSubmit,
  saving,
  applePay,
  collectBillingDetails,
  billingDetailsPrefill,
}: {
  onSubmit: InAppBillingPanelProps['onSubmit'];
  saving: boolean;
  applePay?: ApplePayOption;
  collectBillingDetails: boolean;
  billingDetailsPrefill: BillingDetailsPrefill;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  return (
    <RecoverPaymentForm
      onSuccess={(tokenId) => stripe && onSubmit(tokenId, stripe)}
      loading={saving}
      submitLabel={t('update')}
      applePay={applePay}
      collectBillingDetails={collectBillingDetails}
      hideLink
      layout={EXPANDED_LAYOUT}
      billingDetailsPrefill={billingDetailsPrefill}
    />
  );
}

/**
 * Without a shipping address on file the server has no address to attach to the card, so the panel
 * collects a billing address, which the server also stores as the shipping address. With one on file
 * it collects none, so the account address is used and stays untouched.
 */
export default function InAppBillingPanel({
  hasShipping,
  hasBilling,
  billingDetailsPrefill,
  onSubmit,
  onCancel,
  saving,
}: InAppBillingPanelProps) {
  const { t } = useTranslation();
  const { currency } = useLocale();
  const [applePay, setApplePay] = useState<ApplePayOption>();

  // renewal terms let Apple Pay issue a merchant token that renewals can charge off-session;
  // best-effort, a device token is the pre-existing fallback
  useEffect(() => {
    let cancelled = false;
    webApiClient
      .get<RenewalTerms | null>('/api/ecommerce/renewal-terms')
      .then((response) => {
        if (!cancelled && response.data) setApplePay(buildApplePayRenewalOption(response.data));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <BackIcon onClick={onCancel} className={styles.backButton} />
        <h3>{t(hasBilling ? 'edit_billing' : 'add_payment_method')}</h3>
        <div className={styles.headerPlaceholder} />
      </div>
      <div className={sheetStyles.panelBody}>
        <StripeElementsProvider
          mode="setup"
          currency={currency}
          excludedPaymentMethodTypes={EXCLUDED_PAYMENT_METHOD_TYPES}
        >
          <BillingForm
            onSubmit={onSubmit}
            saving={saving}
            applePay={applePay}
            collectBillingDetails={!hasShipping}
            billingDetailsPrefill={billingDetailsPrefill}
          />
        </StripeElementsProvider>
      </div>
    </div>
  );
}
